import { useState, useMemo } from 'react';
import { Loader2, MoreVertical, Ban, CheckCircle, ShieldCheck, ShieldOff, Trash2, AlertCircle, Filter, Search, ArrowUpDown, X, Pencil, Clock, MessageSquare } from 'lucide-react';
import { FEATURES } from '@/config/appConfig';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { DeleteUserDialog } from './DeleteUserDialog';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { AdminUser, UserStatus, UserRole } from '../types/admin.types';

interface Props {
  users: AdminUser[];
  editingUserId: number | null;
  togglingUserId: number | null;
  updatingRoleUserId: number | null;
  deletingUserId: number | null;
  toggleAuthError: string | null;
  updateRoleError: string | null;
  deleteError: string | null;
  editError: string | null;
  onClearToggleAuthError: () => void;
  onClearUpdateRoleError: () => void;
  onClearDeleteError: () => void;
  onClearEditError: () => void;
  onEditUser: (user: AdminUser) => void;
  onToggleAuthorization: (userId: number, status: UserStatus) => void;
  onUpdateRole: (userId: number, rol: UserRole) => void;
  onDeleteUser: (userId: number) => void;
}

const LAST_SEEN_OPTIONS = [
  'Just now', '45m ago', '2h ago', '5h ago', 'Yesterday',
  '2d ago', '4d ago', '1 week ago', '2 weeks ago', '1 month ago',
];

const getDemoLastSeen = (userId: number) =>
  LAST_SEEN_OPTIONS[userId % LAST_SEEN_OPTIONS.length];

const getDemoInteractions = (userId: number) =>
  ((userId * 47 + 13) % 246) + 5;

type FilterType = 'all' | 'active' | 'suspended' | 'admins' | 'users';
type SortType = 'name-asc' | 'name-desc' | 'email-asc' | 'email-desc';

const filterLabels: Record<FilterType, string> = {
  all: 'All users',
  active: 'Active only',
  suspended: 'Suspended only',
  admins: 'Admins only',
  users: 'Regular users only',
};

const sortLabels: Record<SortType, string> = {
  'name-asc': 'Name (A-Z)',
  'name-desc': 'Name (Z-A)',
  'email-asc': 'Email (A-Z)',
  'email-desc': 'Email (Z-A)',
};

export const UserTable = ({
  users,
  editingUserId,
  togglingUserId,
  updatingRoleUserId,
  deletingUserId,
  toggleAuthError,
  updateRoleError,
  deleteError,
  editError,
  onClearToggleAuthError,
  onClearUpdateRoleError,
  onClearDeleteError,
  onClearEditError,
  onEditUser,
  onToggleAuthorization,
  onUpdateRole,
  onDeleteUser,
}: Props) => {
  const { isSuperAdmin, appUser } = useAuth();
  const [pendingDelete, setPendingDelete] = useState<AdminUser | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [sortBy, setSortBy] = useState<SortType>('name-asc');
  const [searchQuery, setSearchQuery] = useState('');

  const handleDeleteConfirm = () => {
    if (!pendingDelete) return;
    onDeleteUser(pendingDelete.user_id);
    setPendingDelete(null);
  };

  // Filter, search, and sort users
  const processedUsers = useMemo(() => {
    let result = [...users];

    // Apply filter
    switch (activeFilter) {
      case 'active':
        result = result.filter((u) => u.status === 'active');
        break;
      case 'suspended':
        result = result.filter((u) => u.status === 'inactive');
        break;
      case 'admins':
        result = result.filter((u) => u.user_meta.rol !== 'user');
        break;
      case 'users':
        result = result.filter((u) => u.user_meta.rol === 'user');
        break;
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (u) =>
          (u.name || '').toLowerCase().includes(query) ||
          (u.email || '').toLowerCase().includes(query)
      );
    }

    // Apply sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name-desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'email-asc':
          return (a.email || '').localeCompare(b.email || '');
        case 'email-desc':
          return (b.email || '').localeCompare(a.email || '');
        default:
          return 0;
      }
    });

    return result;
  }, [users, activeFilter, sortBy, searchQuery]);

  if (users.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
        No users found.
      </div>
    );
  }

  return (
    <>
      {/* Toolbar: Search, Filter, Sort */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 pb-4 border-b border-slate-200">
        {/* Search input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-base border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filter dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="w-full sm:w-auto">
              <Filter className="h-4 w-4 mr-2" />
              {filterLabels[activeFilter]}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Filter by</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {(Object.keys(filterLabels) as FilterType[]).map((filter) => (
              <DropdownMenuItem
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={activeFilter === filter ? 'bg-slate-100' : ''}
              >
                {filterLabels[filter]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Sort dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="w-full sm:w-auto">
              <ArrowUpDown className="h-4 w-4 mr-2" />
              {sortLabels[sortBy]}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Sort by</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {(Object.keys(sortLabels) as SortType[]).map((sort) => (
              <DropdownMenuItem
                key={sort}
                onClick={() => setSortBy(sort)}
                className={sortBy === sort ? 'bg-slate-100' : ''}
              >
                {sortLabels[sort]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Result count */}
      <div className="text-sm text-slate-500 mb-3">
        Showing {processedUsers.length} of {users.length} user{users.length !== 1 ? 's' : ''}
      </div>

      {/* Dismissable inline error banners */}
      {editError && (
        <ErrorBanner message={editError} onDismiss={onClearEditError} />
      )}
      {toggleAuthError && (
        <ErrorBanner message={toggleAuthError} onDismiss={onClearToggleAuthError} />
      )}
      {updateRoleError && (
        <ErrorBanner message={updateRoleError} onDismiss={onClearUpdateRoleError} />
      )}
      {deleteError && (
        <ErrorBanner message={deleteError} onDismiss={onClearDeleteError} />
      )}

      {processedUsers.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
          No users match your search or filter.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {processedUsers.map((user) => {
                  const isSelf = user.user_id === appUser?.user_id;
                  const isEditingThis = editingUserId === user.user_id;
                  const isTogglingThis = togglingUserId === user.user_id;
                  const isUpdatingRoleThis = updatingRoleUserId === user.user_id;
                  const isDeletingThis = deletingUserId === user.user_id;
                  const isLoadingAny = isEditingThis || isTogglingThis || isUpdatingRoleThis || isDeletingThis;

                  return (
                    <tr key={user.user_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">
                          {user.name || '—'}
                          {isSelf && (
                            <span className="ml-2 text-xs text-slate-400">(you)</span>
                          )}
                        </div>
                        {/* {FEATURES.ANALYTICS_PANEL && (
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="flex items-center gap-1 text-xs text-slate-400">
                              <Clock className="h-3 w-3" />
                              {getDemoLastSeen(user.user_id)}
                            </span>
                            <span className="flex items-center gap-1 text-xs text-slate-400">
                              <MessageSquare className="h-3 w-3" />
                              {getDemoInteractions(user.user_id)} interactions
                            </span>
                          </div>
                        )} */}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{user.email}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={user.status} />
                      </td>
                      <td className="px-4 py-3">
                        <RoleBadge rol={user.user_meta.rol} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end">
                          {isSelf ? (
                            <span className="text-xs text-slate-400">No actions available</span>
                          ) : (
                            <DropdownMenu modal={false}>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={isLoadingAny}
                                  className="h-8 px-2"
                                >
                                  {isLoadingAny ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <>
                                      <MoreVertical className="h-4 w-4" />
                                      <span className="ml-1 text-xs">Actions</span>
                                    </>
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                {/* Edit */}
                                {FEATURES.ADMIN_EDIT_USER && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => onEditUser(user)}
                                      disabled={isEditingThis}
                                    >
                                      <Pencil className="h-4 w-4 mr-2 text-slate-600" />
                                      <span>Edit user</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}

                                {/* Suspend / Activate */}
                                <DropdownMenuItem
                                  onClick={() =>
                                    onToggleAuthorization(
                                      user.user_id,
                                      user.status === 'active' ? 'inactive' : 'active',
                                    )
                                  }
                                  disabled={isTogglingThis}
                                >
                                  {user.status === 'active' ? (
                                    <>
                                      <Ban className="h-4 w-4 mr-2 text-red-600" />
                                      <span>Suspend user</span>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                      <span>Activate user</span>
                                    </>
                                  )}
                                </DropdownMenuItem>

                                {/* Edit role — super_admin only */}
                                {isSuperAdmin && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() =>
                                        user.user_meta.rol !== 'super_admin' &&
                                        onUpdateRole(
                                          user.user_id,
                                          user.user_meta.rol === 'admin' ? 'user' : 'admin',
                                        )
                                      }
                                      disabled={
                                        isUpdatingRoleThis ||
                                        user.user_meta.rol === 'super_admin' ||
                                        (user.status !== 'active' && user.user_meta.rol === 'user')
                                      }
                                    >
                                      {user.user_meta.rol === 'super_admin' ? (
                                        <>
                                          <ShieldCheck className="h-4 w-4 mr-2 text-slate-400" />
                                          <span className="text-slate-400">Super Admin (cannot change)</span>
                                        </>
                                      ) : user.user_meta.rol === 'admin' ? (
                                        <>
                                          <ShieldOff className="h-4 w-4 mr-2 text-slate-600" />
                                          <span>Remove admin role</span>
                                        </>
                                      ) : (
                                        <>
                                          <ShieldCheck className="h-4 w-4 mr-2 text-blue-600" />
                                          <span>
                                            Grant admin role
                                            {user.status !== 'active' && (
                                              <span className="block text-xs text-slate-400 mt-0.5">
                                                (activate user first)
                                              </span>
                                            )}
                                          </span>
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  </>
                                )}

                                {/* Delete — super_admin only */}
                                {isSuperAdmin && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => setPendingDelete(user)}
                                      disabled={isDeletingThis}
                                      className="text-red-600 focus:text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      <span>Delete user</span>
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden flex flex-col gap-3">
            {processedUsers.map((user) => {
              const isSelf = user.user_id === appUser?.user_id;
              const isEditingThis = editingUserId === user.user_id;
              const isTogglingThis = togglingUserId === user.user_id;
              const isUpdatingRoleThis = updatingRoleUserId === user.user_id;
              const isDeletingThis = deletingUserId === user.user_id;
              const isLoadingAny = isEditingThis || isTogglingThis || isUpdatingRoleThis || isDeletingThis;

              return (
                <div
                  key={user.user_id}
                  className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col gap-3"
                >
                  <div>
                    <p className="font-medium text-slate-800">
                      {user.name || '—'}
                      {isSelf && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                    </p>
                    <p className="text-sm text-slate-500">{user.email}</p>
                    {FEATURES.ANALYTICS_PANEL && (
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock className="h-3 w-3" />
                          {getDemoLastSeen(user.user_id)}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <MessageSquare className="h-3 w-3" />
                          {getDemoInteractions(user.user_id)} interactions
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Status, Role, and Actions on same line */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={user.status} />
                    <RoleBadge rol={user.user_meta.rol} />

                    {!isSelf && (
                      <div className="ml-auto">
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={isLoadingAny}
                              className="h-7 px-2"
                            >
                              {isLoadingAny ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <MoreVertical className="h-3 w-3" />
                                  <span className="ml-1 text-xs">Actions</span>
                                </>
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {/* Edit */}
                            {FEATURES.ADMIN_EDIT_USER && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => onEditUser(user)}
                                  disabled={isEditingThis}
                                >
                                  <Pencil className="h-4 w-4 mr-2 text-slate-600" />
                                  <span>Edit user</span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}

                            {/* Suspend / Activate */}
                            <DropdownMenuItem
                              onClick={() =>
                                onToggleAuthorization(
                                  user.user_id,
                                  user.status === 'active' ? 'inactive' : 'active',
                                )
                              }
                              disabled={isTogglingThis}
                            >
                              {user.status === 'active' ? (
                                <>
                                  <Ban className="h-4 w-4 mr-2 text-red-600" />
                                  <span>Suspend user</span>
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                  <span>Activate user</span>
                                </>
                              )}
                            </DropdownMenuItem>

                            {/* Edit role — super_admin only */}
                            {isSuperAdmin && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() =>
                                    user.user_meta.rol !== 'super_admin' &&
                                    onUpdateRole(
                                      user.user_id,
                                      user.user_meta.rol === 'admin' ? 'user' : 'admin',
                                    )
                                  }
                                  disabled={
                                    isUpdatingRoleThis ||
                                    user.user_meta.rol === 'super_admin' ||
                                    (user.status !== 'active' && user.user_meta.rol === 'user')
                                  }
                                >
                                  {user.user_meta.rol === 'super_admin' ? (
                                    <>
                                      <ShieldCheck className="h-4 w-4 mr-2 text-slate-400" />
                                      <span className="text-slate-400">Super Admin (cannot change)</span>
                                    </>
                                  ) : user.user_meta.rol === 'admin' ? (
                                    <>
                                      <ShieldOff className="h-4 w-4 mr-2 text-slate-600" />
                                      <span>Remove admin role</span>
                                    </>
                                  ) : (
                                    <>
                                      <ShieldCheck className="h-4 w-4 mr-2 text-blue-600" />
                                      <span>
                                        Grant admin role
                                        {user.status !== 'active' && (
                                          <span className="block text-xs text-slate-400 mt-0.5">
                                            (activate user first)
                                          </span>
                                        )}
                                      </span>
                                    </>
                                  )}
                                </DropdownMenuItem>
                              </>
                            )}

                            {/* Delete — super_admin only */}
                            {isSuperAdmin && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setPendingDelete(user)}
                                  disabled={isDeletingThis}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  <span>Delete user</span>
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <DeleteUserDialog
        user={pendingDelete}
        isDeleting={deletingUserId === pendingDelete?.user_id}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────

const ErrorBanner = ({ message, onDismiss }: { message: string; onDismiss: () => void }) => (
  <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
    <AlertCircle className="h-4 w-4 shrink-0" />
    <span className="flex-1">{message}</span>
    <button
      onClick={onDismiss}
      className="ml-1 p-0.5 rounded hover:bg-red-100 transition-colors"
      aria-label="Dismiss error"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  </div>
);

const StatusBadge = ({ status }: { status: UserStatus }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
      status === 'active'
        ? 'bg-green-100 text-green-700 border border-green-200'
        : 'bg-red-100 text-red-700 border border-red-200'
    }`}
  >
    {status === 'active' ? 'Active' : 'Suspended'}
  </span>
);

const RoleBadge = ({ rol }: { rol: UserRole }) => {
  if (rol === 'super_admin') {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
        Super Admin
      </span>
    );
  }
  if (rol === 'admin') {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
        Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
      User
    </span>
  );
};
